







































































































































import Vue from 'vue'
import { PATIENT_NOUN } from '@/constants/app-variables'
import { ShiftReportShiftPeriods as ShiftPeriods } from '@/constants/shift-and-round-reports'
import MetricTextLabel from '@/components/shift-and-round-reports/components/MetricTextLabel.vue'
import MetricChangeLabel from '@/components/shift-and-round-reports/components/MetricChangeLabel.vue'
import VitalSignStats from '@/components/shift-and-round-reports/components/VitalSignStats.vue'

const enum EventNames {
  Fall = 'Falls',
  Elop = 'Elop.',
  AttemptedElop = 'ATPT. Elop./Location',
  VitalSigns = 'Vital Signs',
  SleepChanges = 'Sleep Changes',
  ActivityChanges = 'Activity Changes',
  FallRisk = 'High Risk of Fall',
  WoundRisk = 'High Risk of Wounds',
  UtiRisk = 'High Risk of UTI',
}

const enum TableItemEventKeys {
  Fall = 'fall',
  Elop = 'elop',
  AttemptedElop = 'attemptedElop',
  VitalSigns = 'vitalSigns',
  SleepChanges = 'sleepChanges',
  ActivityChanges = 'activityChanges',
  FallRisk = 'fallRisk',
  WoundRisk = 'woundRisk',
  UtiRisk = 'utiRisk',
}

export default Vue.extend({
  components: {
    MetricTextLabel,
    MetricChangeLabel,
    VitalSignStats,
  },
  props: {
    shiftPeriod: {
      type: String,
      required: true,
    },
    events: {
      type: Array,
      default: () => [],
    },
    showLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      riskNames: [EventNames.FallRisk, EventNames.WoundRisk, EventNames.UtiRisk],
      loadingText: 'Generating shift report. This may take a few seconds...',
    }
  },

  computed: {
    isMorningShift() {
      return this.shiftPeriod === ShiftPeriods.Morning
    },
    eventNames() {
      return [
        EventNames.Fall,
        EventNames.Elop,
        EventNames.AttemptedElop,
        EventNames.VitalSigns,
        this.isMorningShift ? EventNames.SleepChanges : EventNames.ActivityChanges,
        EventNames.FallRisk,
        EventNames.WoundRisk,
        EventNames.UtiRisk,
      ]
    },
    tableItemEventKeys() {
      return [
        TableItemEventKeys.Fall,
        TableItemEventKeys.Elop,
        TableItemEventKeys.AttemptedElop,
        TableItemEventKeys.VitalSigns,
        this.isMorningShift ? TableItemEventKeys.SleepChanges : TableItemEventKeys.ActivityChanges,
        TableItemEventKeys.FallRisk,
        TableItemEventKeys.WoundRisk,
        TableItemEventKeys.UtiRisk,
      ]
    },
    incidentNames() {
      return [
        EventNames.Fall,
        EventNames.Elop,
        EventNames.AttemptedElop,
        EventNames.VitalSigns,
        this.isMorningShift ? EventNames.SleepChanges : EventNames.ActivityChanges,
      ]
    },
    tableHeaders() {
      return [
        {
          text: EventNames.Fall,
          value: TableItemEventKeys.Fall,
        },
        {
          text: EventNames.Elop,
          value: TableItemEventKeys.Elop,
        },
        {
          text: EventNames.AttemptedElop,
          value: TableItemEventKeys.AttemptedElop,
        },
        {
          text: EventNames.VitalSigns,
          value: TableItemEventKeys.VitalSigns,
        },
        {
          text: this.isMorningShift ? EventNames.SleepChanges : EventNames.ActivityChanges,
          value: this.isMorningShift ? TableItemEventKeys.SleepChanges : TableItemEventKeys.ActivityChanges,
        },
        {
          text: EventNames.FallRisk,
          value: TableItemEventKeys.FallRisk,
        },
        {
          text: EventNames.WoundRisk,
          value: TableItemEventKeys.WoundRisk,
        },
        {
          text: EventNames.UtiRisk,
          value: TableItemEventKeys.UtiRisk,
        },
      ].map(header => ({
        ...header,
        divider: true,
        class: [
          'text-uppercase',
          'steel-blue--text',
          [EventNames.FallRisk, EventNames.WoundRisk, EventNames.UtiRisk].includes(header.text)
            ? 'coral'
            : 'light-grey',
        ],
      }))
    },
    eventsInColumnOrder() {
      const orderedEvents = []
      this.eventNames.forEach(eventName => {
        const event = this.events.find(event => event.name === eventName)
        orderedEvents.push(event ? event : { name: eventName, rooms: [] })
      })
      return orderedEvents.map((event, index) => {
        return {
          ...event,
          columnIndex: index,
          //mapping to avoid state mutation through sort function
          rooms: event.rooms
            .map(room => room)
            .sort((a, b) => {
              return parseInt(a.roomNumber) > parseInt(b.roomNumber) ? 1 : -1
            }),
        }
      })
    },
    totalTableRows() {
      const roomsPerEvent = this.events
        .filter(event => this.eventNames.includes(event.name))
        .map(event => event.rooms.length || 0)
      return Math.max(...roomsPerEvent)
    },
    totalRoomsPerEvent() {
      return this.eventsInColumnOrder.map(incident => ({
        name: incident.name,
        totalRooms: incident.rooms.length,
        isTotal: true,
      }))
    },
    eventsForTable() {
      if (this.showLoading) return []
      const mappedRowsForTable = []
      let i = 0
      while (i < this.totalTableRows) {
        const eventRow = {}
        this.tableItemEventKeys.forEach((key, index) => {
          eventRow[key] = this.eventsInColumnOrder[index].rooms[i] || {}
        })
        mappedRowsForTable.push(eventRow)
        i++
      }
      const totalsRow = {}
      this.tableItemEventKeys.forEach((key, index) => {
        totalsRow[key] = this.totalRoomsPerEvent[index]
      })
      mappedRowsForTable.push(totalsRow)
      return mappedRowsForTable
    },
  },
  methods: {
    highlightTotalsRow(item) {
      if (item.fall.isTotal) {
        return 'light-yellow'
      }
    },
    getTooltipText(item, index) {
      const tooltipEventNames = [
        'fall',
        'elop',
        'attempted elop',
        'vital sign change',
        this.isMorningShift ? 'sleep change' : 'activity change',
        'high risk of fall',
        'high risk of wound',
        'high risk of UTI',
      ]
      const isPlural = item.totalRooms > 1 ? true : false
      const patientNoun = `${PATIENT_NOUN.toLowerCase()}${isPlural ? 's' : ''}`
      if (index >= 5) {
        if (item.totalRooms === 0) {
          return `No ${patientNoun}s are at ${tooltipEventNames[index]}`
        }
        return `${item.totalRooms} ${patientNoun} ${isPlural ? 'are' : 'is'} at ${tooltipEventNames[index]}`
      }
      if (item.totalRooms === 0) {
        return `No ${tooltipEventNames[index]}s were reported during the previous shift period`
      }
      return `${item.totalRooms} ${patientNoun} reported at least one ${tooltipEventNames[index]} during the previous shift period`
    },
    getTooltipColor(item, index) {
      if (item.totalRooms === 0) {
        return 'green'
      }
      if (index >= 5) {
        return 'red'
      }
      return 'primary'
    },
  },
})
